import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "sonner";

const AuthContext = createContext(null);
const TOKEN_KEY = "umakant_token";
const USER_KEY = "umakant_user";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [authing, setAuthing] = useState(false);
  const wallet = useWallet();

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    const u = localStorage.getItem(USER_KEY);
    if (t) setToken(t);
    if (u) { try { setUser(JSON.parse(u)); } catch (e) { /* ignore */ } }
    setHydrated(true);
  }, []);

  const authenticate = useCallback(async (referrer = null, walletOverride = null) => {
    // Allow caller to pass freshly-connected wallet data (avoids React state race)
    const addr = walletOverride?.address || wallet.address;
    const signFn = walletOverride?.signMessage
      || (walletOverride?.signer ? (msg) => walletOverride.signer.signMessage(msg) : null)
      || wallet.signMessage;

    if (!addr) {
      toast.error("Connect your wallet first");
      return;
    }
    setAuthing(true);
    try {
      const nonceResp = await api.post("/auth/nonce", { address: addr });
      const message = nonceResp.data.message;
      const signature = await signFn(message);
      const verifyResp = await api.post("/auth/verify", {
        address: addr,
        signature,
        referrer,
      });
      const { token: newToken, user: newUser } = verifyResp.data;
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(USER_KEY, JSON.stringify(newUser));
      setToken(newToken);
      setUser(newUser);
      toast.success("Welcome to SMART HUB!");
      return { token: newToken, user: newUser };
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.shortMessage || e?.message || "Authentication failed";
      toast.error(detail);
      throw e;
    } finally {
      setAuthing(false);
    }
  }, [wallet]);

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/user/me");
      setUser(r.data);
      localStorage.setItem(USER_KEY, JSON.stringify(r.data));
      return r.data;
    } catch (e) {
      /* ignore */
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    wallet.disconnect();
  }, [wallet]);

  const value = { token, user, hydrated, authing, authenticate, refreshUser, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}
